import type { CenaTabletop, MaterialAbsorvente, ObjetoTabletop } from '../tipos'
import { ROTULOS_MOTIVO_AJUSTE } from './operacoesAtendimento'
import type {
  AjusteManualRisco,
  EstadoRiscoNaSessao,
  MotivoAjusteManual,
  EventoHistoricoAtendimento,
  RegistroAcaoRisco,
  SessaoAtendimentoTabletop,
  SituacaoRisco,
  TipoAcaoRisco,
} from './tipos'

/**
 * Riscos da cena como elementos operacionais: vidro, derramamento e risco
 * elétrico. A configuração vem da cena (preparação); o estado é calculado a
 * partir das ações que o instrutor registrou na sessão — nada some porque
 * alguém excluiu o objeto no editor, e pedir apoio não resolve um risco.
 */

export type TipoRisco = 'vidro' | 'derramamento' | 'eletrico' | 'sangue' | 'fogo'

export const MATERIAIS_PADRAO_DERRAMAMENTO: readonly MaterialAbsorvente[] = ['serragem', 'areia', 'absorvente_industrial']

export const ROTULOS_TIPO_RISCO: Readonly<Record<TipoRisco, string>> = {
  vidro: 'Vidro na área de trabalho',
  derramamento: 'Derramamento',
  eletrico: 'Risco elétrico',
  sangue: 'Risco biológico (sangue)',
  fogo: 'Fogo / Princípio de incêndio',
}

export const ROTULOS_SITUACAO_RISCO: Readonly<Record<SituacaoRisco, string>> = {
  ativo: 'Ativo',
  parcial: 'Parcialmente controlado',
  controlado: 'Controlado',
}

export const ROTULOS_ACAO_RISCO: Readonly<Record<TipoAcaoRisco, string>> = {
  varrer_vidro: 'Varrer fragmentos',
  cobrir_vidro: 'Cobrir com manta',
  isolar_area: 'Isolar a área',
  conter_vazamento: 'Conter o vazamento',
  aplicar_absorvente: 'Aplicar absorvente',
  acionar_concessionaria: 'Acionar concessionária',
  confirmar_desligamento: 'Concessionária confirmou desligamento',
  combater_incendio: 'Combater incêndio (extintor / mangueira)',
}

export const ROTULOS_MATERIAL: Readonly<Record<MaterialAbsorvente, string>> = {
  serragem: 'Serragem',
  areia: 'Areia',
  absorvente_industrial: 'Absorvente industrial',
}

/** Botões que cada tipo de risco oferece, na ordem em que aparecem. */
export const ACOES_POR_RISCO: Readonly<Record<TipoRisco, readonly TipoAcaoRisco[]>> = {
  vidro: ['varrer_vidro', 'cobrir_vidro'],
  derramamento: ['isolar_area', 'conter_vazamento', 'aplicar_absorvente'],
  eletrico: ['isolar_area', 'acionar_concessionaria', 'confirmar_desligamento'],
  sangue: [],
  fogo: ['isolar_area', 'combater_incendio'],
}

export const ESTADO_RISCO_VAZIO: EstadoRiscoNaSessao = { identificado: false, acoes: [] }

export function tipoDeRisco(objeto: ObjetoTabletop): TipoRisco | null {
  if (objeto.tipo === 'detritosVidro') return 'vidro'
  if (objeto.tipo === 'derramamento') return 'derramamento'
  if (objeto.tipo === 'caboEletrico') return 'eletrico'
  if (objeto.tipo === 'manchaSangue') return 'sangue'
  if (objeto.tipo === 'fogo') return 'fogo'
  return null
}

export function estadoDoRisco(sessao: SessaoAtendimentoTabletop, idObjeto: string): EstadoRiscoNaSessao {
  return sessao.riscos[idObjeto] ?? ESTADO_RISCO_VAZIO
}

/** Materiais aceitos por esta mancha; `null` = lista vazia (efeito a definir pelo instrutor). */
function materiaisAceitos(objeto: ObjetoTabletop): readonly MaterialAbsorvente[] | null {
  const lista = objeto.risco?.materiaisAceitos ?? MATERIAIS_PADRAO_DERRAMAMENTO
  return lista.length > 0 ? lista : null
}

/** Resultado de UMA aplicação de absorvente, para registro e debriefing. */
export type ResultadoAbsorvente = 'aceito' | 'nao_previsto' | 'a_definir'

export function resultadoDoAbsorvente(objeto: ObjetoTabletop, material: MaterialAbsorvente | undefined): ResultadoAbsorvente {
  const aceitos = materiaisAceitos(objeto)
  if (!aceitos) return 'a_definir'
  return material && aceitos.includes(material) ? 'aceito' : 'nao_previsto'
}

export interface SituacaoCalculadaRisco {
  readonly tipo: TipoRisco
  readonly situacao: SituacaoRisco
  /** frases curtas do que já foi feito ou falta — para o instrutor e o debriefing */
  readonly detalhes: readonly string[]
  /** presente quando o instrutor ajustou à mão: vale `situacao`, e isto é o que as ações dariam */
  readonly ajuste?: { readonly situacaoCalculada: SituacaoRisco; readonly motivo: MotivoAjusteManual }
}

/** Último ajuste manual ainda valendo (um "voltar ao cálculo" encerra os anteriores). */
export function ajusteManualVigente(estado: EstadoRiscoNaSessao): AjusteManualRisco | null {
  const ultimo = estado.ajustesManuais?.at(-1)
  return ultimo && ultimo.situacaoDepois !== null ? ultimo : null
}

const fez = (estado: EstadoRiscoNaSessao, tipo: TipoAcaoRisco) => estado.acoes.some((a) => a.tipo === tipo)

/**
 * Situação atual do risco: a do ajuste manual do instrutor, se houver um
 * valendo; senão, a calculada pelas ações. O ajuste vale até o instrutor
 * voltar ao cálculo — ações registradas depois dele não o desfazem sozinhas.
 */
export function situacaoDoRisco(objeto: ObjetoTabletop, estado: EstadoRiscoNaSessao): SituacaoCalculadaRisco | null {
  const calculada = situacaoCalculadaDoRisco(objeto, estado)
  const ajuste = ajusteManualVigente(estado)
  if (!calculada || !ajuste?.situacaoDepois) return calculada
  return {
    ...calculada,
    situacao: ajuste.situacaoDepois,
    detalhes: [...calculada.detalhes, `Ajustado pelo instrutor (${ROTULOS_MOTIVO_AJUSTE[ajuste.motivo].toLowerCase()})`],
    ajuste: { situacaoCalculada: calculada.situacao, motivo: ajuste.motivo },
  }
}

/**
 * Situação do risco a partir das ações registradas:
 *
 * - vidro: varrer controla; cobrir com manta é controle parcial;
 * - derramamento: controlado com absorvente aceito e vazamento contido (ou
 *   sem vazamento ativo); absorvente com o vazamento ainda ativo, ou só
 *   isolar/conter, é parcial; material não previsto não conta;
 * - elétrico: isolar e acionar a concessionária são parciais (a rede continua
 *   energizada); só a confirmação do desligamento, depois de acionada, controla.
 */
function situacaoCalculadaDoRisco(objeto: ObjetoTabletop, estado: EstadoRiscoNaSessao): SituacaoCalculadaRisco | null {
  const tipo = tipoDeRisco(objeto)
  if (!tipo) return null
  const detalhes: string[] = []

  if (tipo === 'vidro') {
    if (fez(estado, 'varrer_vidro')) return { tipo, situacao: 'controlado', detalhes: ['Fragmentos varridos da área de trabalho'] }
    if (fez(estado, 'cobrir_vidro')) return { tipo, situacao: 'parcial', detalhes: ['Fragmentos cobertos, ainda na área'] }
    return { tipo, situacao: 'ativo', detalhes: ['Fragmentos de vidro na área de trabalho'] }
  }

  if (tipo === 'derramamento') {
    const vazando = Boolean(objeto.risco?.vazamentoAtivo) && !fez(estado, 'conter_vazamento')
    const absorvido = estado.acoes.some(
      (a) => a.tipo === 'aplicar_absorvente' && resultadoDoAbsorvente(objeto, a.material) === 'aceito',
    )
    const aDefinir = estado.acoes.some(
      (a) => a.tipo === 'aplicar_absorvente' && resultadoDoAbsorvente(objeto, a.material) === 'a_definir',
    )
    if (objeto.risco?.vazamentoAtivo) detalhes.push(vazando ? 'Vazamento ainda ativo' : 'Vazamento contido na fonte')
    if (absorvido) detalhes.push('Absorvente aplicado')
    if (aDefinir) detalhes.push('Absorvente aplicado — efeito a definir pelo instrutor (nenhum material previsto)')
    if (estado.acoes.some((a) => a.tipo === 'aplicar_absorvente' && resultadoDoAbsorvente(objeto, a.material) === 'nao_previsto')) {
      detalhes.push('Material aplicado não previsto nesta cena')
    }
    if (fez(estado, 'isolar_area')) detalhes.push('Área isolada')

    if (absorvido && !vazando) return { tipo, situacao: 'controlado', detalhes }
    const algumaAcao = absorvido || fez(estado, 'isolar_area') || fez(estado, 'conter_vazamento')
    return { tipo, situacao: algumaAcao ? 'parcial' : 'ativo', detalhes: detalhes.length ? detalhes : ['Produto derramado na via'] }
  }

  // elétrico
  const acionada = fez(estado, 'acionar_concessionaria')
  const confirmadoDepoisDeAcionar = estado.acoes.some(
    (a, i) => a.tipo === 'confirmar_desligamento' && estado.acoes.slice(0, i).some((b) => b.tipo === 'acionar_concessionaria'),
  )
  if (fez(estado, 'isolar_area')) detalhes.push('Área isolada')
  if (acionada) detalhes.push('Concessionária acionada — rede ainda energizada até a confirmação')
  if (fez(estado, 'confirmar_desligamento') && !confirmadoDepoisDeAcionar) {
    detalhes.push('Confirmação registrada sem a concessionária ter sido acionada — não conta')
  }
  if (confirmadoDepoisDeAcionar) return { tipo, situacao: 'controlado', detalhes: [...detalhes, 'Desligamento confirmado: condição segura'] }
  if (tipo === 'eletrico') {
    return {
      tipo,
      situacao: fez(estado, 'isolar_area') || acionada ? 'parcial' : 'ativo',
      detalhes: detalhes.length ? detalhes : ['Cabo possivelmente energizado'],
    }
  }

  // fogo / princípio de incêndio
  if (tipo === 'fogo') {
    if (fez(estado, 'combater_incendio')) {
      return { tipo, situacao: 'controlado', detalhes: ['Incêndio combatido e extinto com extintor/linha'] }
    }
    if (fez(estado, 'isolar_area')) {
      return { tipo, situacao: 'parcial', detalhes: ['Área com foco de incêndio isolada, aguardando extinção'] }
    }
    return { tipo, situacao: 'ativo', detalhes: ['Foco de incêndio ativo'] }
  }

  // sangue
  return {
    tipo,
    situacao: estado.identificado ? 'controlado' : 'ativo',
    detalhes: [
      estado.identificado ? 'Identificado na varredura 360°' : 'Não identificado ainda na 360°',
      'Pista de hemorragia grave e biossegurança',
    ],
  }
}

/** O que qualquer um vê na cena — vai para a cena 3D e para a projeção (sem regra nem gabarito). */
export interface AparenciaRisco {
  readonly fragmentosRemovidos?: boolean
  readonly coberto?: boolean
  readonly absorvente?: MaterialAbsorvente
  readonly vazando?: boolean
  readonly areaIsolada?: boolean
  readonly energizado?: boolean
  readonly extinto?: boolean
}

export function aparenciaDoRisco(objeto: ObjetoTabletop, estado: EstadoRiscoNaSessao): AparenciaRisco | null {
  const tipo = tipoDeRisco(objeto)
  if (!tipo) return null
  const ultimoAbsorvente = [...estado.acoes].reverse().find((a) => a.tipo === 'aplicar_absorvente')?.material
  const situacao = situacaoDoRisco(objeto, estado)!
  switch (tipo) {
    case 'vidro':
      return { fragmentosRemovidos: fez(estado, 'varrer_vidro'), coberto: fez(estado, 'cobrir_vidro') }
    case 'derramamento':
      return {
        ...(ultimoAbsorvente ? { absorvente: ultimoAbsorvente } : {}),
        vazando: Boolean(objeto.risco?.vazamentoAtivo) && !fez(estado, 'conter_vazamento'),
        areaIsolada: fez(estado, 'isolar_area'),
      }
    case 'eletrico':
      return { areaIsolada: fez(estado, 'isolar_area'), energizado: situacao.situacao !== 'controlado' }
    case 'sangue':
      return { areaIsolada: false }
    case 'fogo':
      return { areaIsolada: fez(estado, 'isolar_area'), extinto: situacao.situacao === 'controlado' }
  }
}

/** Aparência de todos os riscos visíveis da cena — o mapa que a cena 3D e a projeção recebem. */
export function aparenciasDosRiscos(
  cena: CenaTabletop,
  sessao: SessaoAtendimentoTabletop,
): Record<string, AparenciaRisco> {
  const mapa: Record<string, AparenciaRisco> = {}
  for (const objeto of cena.objetos) {
    if (objeto.oculto) continue
    const aparencia = aparenciaDoRisco(objeto, estadoDoRisco(sessao, objeto.id))
    if (aparencia) mapa[objeto.id] = aparencia
  }
  return mapa
}

function novoId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `risco-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/**
 * Registra uma ação num risco. Registrar já marca o risco como identificado
 * (a equipe só age sobre o que encontrou). O objeto é passado para o evento
 * do histórico descrever o resultado do absorvente.
 */
export function registrarAcaoRisco(
  sessao: SessaoAtendimentoTabletop,
  objeto: ObjetoTabletop,
  tipo: TipoAcaoRisco,
  material?: MaterialAbsorvente,
): SessaoAtendimentoTabletop {
  const tipoRisco = tipoDeRisco(objeto)
  if (!tipoRisco || !ACOES_POR_RISCO[tipoRisco].includes(tipo)) return sessao
  if (tipo === 'aplicar_absorvente' && !material) return sessao

  const estado = estadoDoRisco(sessao, objeto.id)
  const acao: RegistroAcaoRisco = {
    id: novoId(),
    tipo,
    ...(material ? { material } : {}),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
  }
  const novoEstado: EstadoRiscoNaSessao = { ...estado, identificado: true, acoes: [...estado.acoes, acao] }
  const situacao = situacaoDoRisco(objeto, novoEstado)!

  const rotulo = material ? `${ROTULOS_ACAO_RISCO[tipo]} (${ROTULOS_MATERIAL[material].toLowerCase()})` : ROTULOS_ACAO_RISCO[tipo]
  const detalheAbsorvente =
    tipo === 'aplicar_absorvente'
      ? { aceito: '', nao_previsto: 'material não previsto nesta cena', a_definir: 'efeito a definir pelo instrutor' }[
          resultadoDoAbsorvente(objeto, material)
        ]
      : ''

  const evento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'risco',
    objetoVitimaId: objeto.id,
    descricao: `${objeto.nome}: ${rotulo}`,
    detalhes: [`Situação: ${ROTULOS_SITUACAO_RISCO[situacao.situacao]}`, detalheAbsorvente].filter(Boolean).join(' | '),
  }

  return {
    ...sessao,
    riscos: { ...sessao.riscos, [objeto.id]: novoEstado },
    historicoEventos: [evento, ...sessao.historicoEventos],
  }
}

/** A equipe apontou o risco na volta 360° (sem ter agido ainda). */
export function definirRiscoIdentificado(
  sessao: SessaoAtendimentoTabletop,
  objeto: ObjetoTabletop,
  identificado: boolean,
): SessaoAtendimentoTabletop {
  const estado = estadoDoRisco(sessao, objeto.id)
  if (estado.identificado === identificado) return sessao
  const evento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'risco',
    objetoVitimaId: objeto.id,
    descricao: identificado ? `Risco identificado: ${objeto.nome}` : `Identificação desfeita: ${objeto.nome}`,
  }
  return {
    ...sessao,
    riscos: { ...sessao.riscos, [objeto.id]: { ...estado, identificado } },
    historicoEventos: [evento, ...sessao.historicoEventos],
  }
}

/**
 * Ajuste manual da situação de um risco, com motivo escolhido numa lista —
 * mesmo padrão do ajuste dos problemas da vítima. `null` volta ao cálculo
 * pelas ações. Fica no histórico e no debriefing.
 */
export function ajustarRiscoManualmente(
  sessao: SessaoAtendimentoTabletop,
  objeto: ObjetoTabletop,
  novaSituacao: SituacaoRisco | null,
  motivo: MotivoAjusteManual,
): SessaoAtendimentoTabletop {
  const estado = estadoDoRisco(sessao, objeto.id)
  const atual = situacaoDoRisco(objeto, estado)
  if (!atual) return sessao
  const vigente = ajusteManualVigente(estado)
  if (novaSituacao === null ? !vigente : vigente?.situacaoDepois === novaSituacao) return sessao

  const ajuste: AjusteManualRisco = {
    id: novoId(),
    situacaoAntes: atual.situacao,
    situacaoDepois: novaSituacao,
    motivo,
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
  }
  const novoEstado: EstadoRiscoNaSessao = { ...estado, ajustesManuais: [...(estado.ajustesManuais ?? []), ajuste] }
  const depois = situacaoDoRisco(objeto, novoEstado)!.situacao

  const evento: EventoHistoricoAtendimento = {
    id: novoId(),
    tempoSimuladoSegundos: sessao.tempoSimuladoSegundos,
    tipo: 'ajuste',
    objetoVitimaId: objeto.id,
    descricao:
      novaSituacao === null
        ? `Ajuste manual desfeito — ${objeto.nome}: volta ao cálculo (${ROTULOS_SITUACAO_RISCO[depois]})`
        : `Ajuste manual — ${objeto.nome}: ${ROTULOS_SITUACAO_RISCO[atual.situacao]} → ${ROTULOS_SITUACAO_RISCO[depois]}`,
    ...(novaSituacao === null ? {} : { detalhes: `Motivo: ${ROTULOS_MOTIVO_AJUSTE[motivo]}` }),
  }

  return {
    ...sessao,
    riscos: { ...sessao.riscos, [objeto.id]: novoEstado },
    historicoEventos: [evento, ...sessao.historicoEventos],
  }
}

/** Linha do debriefing para um ajuste manual de risco. */
export function descreverAjusteRisco(ajuste: AjusteManualRisco): string {
  if (ajuste.situacaoDepois === null) return 'Ajuste manual desfeito: volta ao cálculo pelas ações'
  return `Ajuste manual: ${ROTULOS_SITUACAO_RISCO[ajuste.situacaoAntes]} → ${ROTULOS_SITUACAO_RISCO[ajuste.situacaoDepois]} (${ROTULOS_MOTIVO_AJUSTE[ajuste.motivo].toLowerCase()})`
}

export interface PendenciaRisco {
  readonly objeto: ObjetoTabletop
  readonly situacao: SituacaoCalculadaRisco
  readonly identificado: boolean
  readonly motivo: string
}

/** Riscos que terminam a sessão sem controle — com a diferença entre "não identificado" e "não resolvido". */
export function pendenciasDeRisco(cena: CenaTabletop, sessao: SessaoAtendimentoTabletop): PendenciaRisco[] {
  const pendencias: PendenciaRisco[] = []
  for (const objeto of cena.objetos) {
    if (objeto.oculto) continue
    const estado = estadoDoRisco(sessao, objeto.id)
    const situacao = situacaoDoRisco(objeto, estado)
    if (!situacao || situacao.situacao === 'controlado') continue
    const motivo = situacao.ajuste
      ? `Ajustado pelo instrutor para ${ROTULOS_SITUACAO_RISCO[situacao.situacao].toLowerCase()} (${ROTULOS_MOTIVO_AJUSTE[situacao.ajuste.motivo].toLowerCase()})`
      : !estado.identificado
      ? 'Não registrado como identificado (não registrado não significa não visto: confira com a equipe)'
      : situacao.tipo === 'eletrico'
        ? 'Sem confirmação de desligamento pela concessionária'
        : `Terminou ${ROTULOS_SITUACAO_RISCO[situacao.situacao].toLowerCase()}`
    pendencias.push({ objeto, situacao, identificado: estado.identificado, motivo })
  }
  return pendencias
}
